/// <amd-module name="Build/Pipelines/PipelinesStore" />

import { BuildStatus } from "Build/Client/Contracts/Build";
import { DeletedPipelineDataList, PipelineData, PipelineDataList, PipelinesSharedData, RunData } from "Build/Client/Contracts/DataProvider";
import { ContributionDataActionCreator, ContributionDataActionHub, DataParser } from "Build/Flux/Actions/ContributionData";
import { ContributionSource } from "Build/Flux/Sources/Contribution";
import { Store } from "Build/Flux/Store";
import { DefinitionFavoritesActionHub, IDefinitionFavoritePayload } from "Build/Pipelines/Actions/Favorites";
import { BuildSignalRActionHub, IBuildUpdatedPayload, IStagesUpdatedPayload } from "Build/Pipelines/Actions/SignalR";
import { PipelineFilterKeys } from "Build/Pipelines/Constants";

import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

import { Filter, IFilterState } from "VSSUI/Utilities/Filter";

export interface IPipelinesStoreProps {
    pageContext: IVssPageContext;
    contributionActionHub: ContributionDataActionHub<PipelineDataList>;
    favoritesActionHub: DefinitionFavoritesActionHub;
    signalRActionHub: BuildSignalRActionHub;
    pipelinesFilter: Filter;
    ignoreInitialData: boolean;
    rootFolder: string;
}

export class PipelinesStore extends Store {
    private static readonly s_batchingDelayInMs = 6 * 1000;

    private _pageContext: IVssPageContext;
    private _source: ContributionSource;
    private _contributionActionCreator: ContributionDataActionCreator<PipelineDataList>;
    private _favoritesActionHub: DefinitionFavoritesActionHub;
    private _signalRActionHub: BuildSignalRActionHub;
    private _pipelines: PipelineData[];
    private _filter: Filter;
    private _loadingData: boolean;
    private _filteredPipelines: PipelineData[];
    private _hasPipelines: boolean;
    private _hasUserDefinedFolders: boolean;
    private _hasDeletedPipelines: boolean | undefined;
    private _runsToUpdate: number[];
    private _updateTimeout: number | undefined;
    private _rootFolder: string;

    constructor(options: IPipelinesStoreProps) {
        super();

        this._pageContext = options.pageContext;
        this._favoritesActionHub = options.favoritesActionHub;
        this._signalRActionHub = options.signalRActionHub;
        this._filter = options.pipelinesFilter;
        this._loadingData = false;
        this._runsToUpdate = [];
        this._updateTimeout = undefined;
        this._rootFolder = options.rootFolder;

        this._contributionActionCreator = new ContributionDataActionCreator({
            pageContext: options.pageContext,
            actionHub: options.contributionActionHub,
            dataParser: this._dataParser
        });

        this._favoritesActionHub.favoriteDefinition.addListener(this._onDefinitionFavorited);
        this._favoritesActionHub.unfavoriteDefinition.addListener(this._onDefinitionUnfavorited);
        this._signalRActionHub.buildUpdated.addListener(this._buildUpdated);
        this._signalRActionHub.stagesUpdated.addListener(this._updateStages);

        this._source = new ContributionSource({
            pageContext: this._pageContext
        });

        let sharedData = this._source.getSharedData("pipelinesSharedData") as PipelinesSharedData;
        this._hasPipelines = sharedData ? sharedData.hasPipelines : false;
        this._hasUserDefinedFolders = sharedData ? sharedData.hasUserDefinedFolders : false;
        this._hasDeletedPipelines = undefined;

        if (this.isListFiltered()) {
            const data = this._source.getData("ms.vss-build-web.pipelines-data-provider") as PipelineDataList;
            if (data) {
                this._filteredPipelines = data.pipelines;
            }
        }
        else if (!options.ignoreInitialData) {
            const data = this._source.getData("ms.vss-build-web.pipelines-data-provider") as PipelineDataList;
            if (data) {
                this._pipelines = data.pipelines;
            }
        }

        this._filter.subscribe(this._onFilterChanged);
    }

    public dispose() {
        super.dispose();
        this._runsToUpdate = [];
        if (this._updateTimeout != undefined) {
            clearTimeout(this._updateTimeout);
            this._updateTimeout = undefined;
        }
        this._favoritesActionHub.favoriteDefinition.removeListener(this._onDefinitionFavorited);
        this._favoritesActionHub.unfavoriteDefinition.removeListener(this._onDefinitionUnfavorited);
        this._signalRActionHub.buildUpdated.removeListener(this._buildUpdated);
        this._signalRActionHub.stagesUpdated.removeListener(this._updateStages);
    }

    public fetchData(refresh: boolean = false) {
        if (refresh || !this._pipelines) {
            if (this.isListFiltered()) {
                this._filterPipelines(this._filter.getFilterItemValue(PipelineFilterKeys.NameFilter) || "");
            }
            else {
                this._loadingData = true;
                const routeValues: { [key: string]: string } = {"option": "recent"};
                this._contributionActionCreator.fetchContributionData("ms.vss-build-web.pipelines-data-provider", routeValues, refresh, this._onInitialDataAvailable);
            }
        }
        this.fetchHasDeletedPipelines();
    }

    public fetchHasDeletedPipelines(refresh: boolean = false) {
        if (refresh || this._hasDeletedPipelines === undefined) {
            const routeValues: { [key: string]: string } = {"existenceCheck": "true"};
            const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.getDataAsync("ms.vss-build-web.deleted-pipelines-data-provider", routeValues, true).then((data: DeletedPipelineDataList) => {
                const currentHasDeletedPipelines = this._hasDeletedPipelines;
                if (data.pipelines && data.pipelines.length > 0) {
                    this._hasDeletedPipelines = true;
                }
                else {
                    this._hasDeletedPipelines = false;
                }
                if (currentHasDeletedPipelines !== this._hasDeletedPipelines) {
                    this.emitChanged();
                }
            });
        }
    }

    public fetchSharedData() {
        this._source.getDataAsync("ms.vss-build-web.pipelines-data-provider", undefined, true).then((data: PipelineDataList) => {
            let sharedData = this._source.getSharedData("pipelinesSharedData") as PipelinesSharedData;
            this._hasPipelines = sharedData ? sharedData.hasPipelines : false;
            this._hasUserDefinedFolders = sharedData ? sharedData.hasUserDefinedFolders : false;
    
            this.emitChanged();
        });
    }

    public hasPipelines() : boolean {
        return !!this._hasPipelines;
    }

    public hasUserDefinedFolders() : boolean {
        return !!this._hasUserDefinedFolders;
    }

    public hasDeletedPipelines(): boolean {
        return (this._hasDeletedPipelines === undefined) ? false : this._hasDeletedPipelines;
    }

    public getPipelines() : PipelineData[] {
        if (this.isListFiltered()) {
            if (!this._filteredPipelines) {
                return [];
            }
            return this._filteredPipelines;
        }
        else {
            if (!this._pipelines) {
                return [];
            }
            return this._pipelines;
        }
    }

    public isListFiltered(): boolean {
        const filterValue = this._filter.getFilterItemValue(PipelineFilterKeys.NameFilter);
        if (filterValue && filterValue != "") {
            return true;
        }
        else {
            return false;
        }
    }

    public isLoadingData(): boolean {
        return this._loadingData;
    }

    public updatePipelineNameAndPath(pipelineId: number, newName: string, newPath: string) {
        if (this._pipelines) {
            this._pipelines.forEach((pipelineData: PipelineData) => {
                if (pipelineData.id === pipelineId) {
                    pipelineData.name = newName;
                    pipelineData.path = newPath;
                }
            });
        }

        if (this._filteredPipelines) {
            this._filteredPipelines.forEach((pipelineData: PipelineData) => {
                if (pipelineData.id === pipelineId) {
                    pipelineData.name = newName;
                    pipelineData.path = newPath;
                }
            });
        }

        this.emitChanged();
    }

    public removePipeline(pipelineId: number) {
        if (this._pipelines) {
            const itemIndex = this._pipelines.findIndex((value: PipelineData) => (value.id === pipelineId));
            if (itemIndex >= 0) {
                this._pipelines.splice(itemIndex, 1);
            }
        }

        if (this._filteredPipelines) {
            const itemIndex = this._filteredPipelines.findIndex((value: PipelineData) => (value.id === pipelineId));
            if (itemIndex >= 0) {
                this._filteredPipelines.splice(itemIndex, 1);
            }
        }

        this.emitChanged();
    }

    private _dataParser: DataParser<PipelineDataList> = (data: PipelineDataList) => {
        return data;
    }

    private _onInitialDataAvailable = (data: PipelineDataList, routeValues?: { [key: string]: string }) => {
        this._pipelines = data.pipelines;
        this._loadingData = false;
        this.emitChanged();
    }

    private _onQueryDataAvailable = (data: PipelineDataList, routeValues?: { [key: string]: string }) => {
        let query: string | undefined = undefined;
        if (routeValues && routeValues["query"]) {
            query = routeValues["query"];
        }
        const filterValue = this._filter.getFilterItemState(PipelineFilterKeys.NameFilter);
        if (query && filterValue !== null && query === filterValue.value) {
            this._filteredPipelines = data.pipelines;
            this._loadingData = false;
            this.emitChanged();
        }
    }

    private _onUpdatedRunsAvailable = (data: PipelineDataList, routeValues?: { [key: string]: string }) => {
        if (data.updatedRuns) {
            data.updatedRuns.forEach((updatedRun: RunData) => {
                this._pipelines.forEach((pipeline: PipelineData) => {
                    if (pipeline.latestRun && updatedRun.id === pipeline.latestRun.id) {
                        pipeline.latestRun.status = updatedRun.status;
                        pipeline.latestRun.result = updatedRun.result;
                        pipeline.latestRun.startTime = updatedRun.startTime;
                        pipeline.latestRun.finishTime = updatedRun.finishTime;
                        pipeline.latestRun.number = updatedRun.number;
                    }
                });
        
                if (this._filteredPipelines) {
                    this._filteredPipelines.forEach((pipeline: PipelineData) => {
                        if (pipeline.latestRun && updatedRun.id === pipeline.latestRun.id) {
                            pipeline.latestRun.status = updatedRun.status;
                            pipeline.latestRun.result = updatedRun.result;
                            pipeline.latestRun.startTime = updatedRun.startTime;
                            pipeline.latestRun.finishTime = updatedRun.finishTime;
                            pipeline.latestRun.number = updatedRun.number;
                        }
                    });
                }

                this.emitChanged();
            });
        }
    }

    private _onDefinitionFavorited = (payload: IDefinitionFavoritePayload) => {
        if (this._pipelines && this._pipelines.length > 0) {
            let changed = false;
            this._pipelines.forEach((pipeline: PipelineData) => {
                if (payload.definition.id === pipeline.id) {
                    pipeline.isFavorite = true;
                    changed = true;
                }
            });

            if (this._filteredPipelines) {
                this._filteredPipelines.forEach((pipeline: PipelineData) => {
                    if (payload.definition.id === pipeline.id) {
                        pipeline.isFavorite = true;
                        changed = true;
                    }
                });
            }

            if (changed) {
                this.emitChanged();
            }
        }
    }

    private _onDefinitionUnfavorited = (payload: IDefinitionFavoritePayload) => {
        if (this._pipelines && this._pipelines.length > 0) {
            let changed = false;
            this._pipelines.forEach((pipeline: PipelineData) => {
                if (payload.definition.id === pipeline.id) {
                    pipeline.isFavorite = false;
                    changed = true;
                }
            });

            if (this._filteredPipelines) {
                this._filteredPipelines.forEach((pipeline: PipelineData) => {
                    if (payload.definition.id === pipeline.id) {
                        pipeline.isFavorite = false;
                        changed = true;
                    }
                });
            }

            if (changed) {
                this.emitChanged();
            }
        }
    }   

    private _onFilterChanged = (value: IFilterState, action: string) => {
        if (action === "filter-applied") {
            const property = value[PipelineFilterKeys.NameFilter];
            this._filterPipelines(property ? property.value : "");
        }
    }

    private _filterPipelines = (filter: string) => {
        if (!filter || filter === "") {
            // clear filter
            this._loadingData = false;
            if (!this._pipelines) {
                this._loadingData = true;
                const routeValues: { [key: string]: string } = {"option": "recent"};
                this._contributionActionCreator.fetchContributionData("ms.vss-build-web.pipelines-data-provider", routeValues, true, this._onInitialDataAvailable);
            }
        }
        else {
            // apply filter
            this._loadingData = true;
            this._contributionActionCreator.fetchContributionData("ms.vss-build-web.pipelines-data-provider", {"query": filter, "rootPath": this._rootFolder}, true, this._onQueryDataAvailable);
        }
        this.emitChanged();
    }

    private _buildUpdated = (payload: IBuildUpdatedPayload) => {
        if (this._pipelines && this._pipelines.length > 0) {
            let matchFound: boolean = false;

            this._pipelines.forEach((pipeline: PipelineData) => {
                if (payload.definitionId === pipeline.id) {
                    if (pipeline.latestRun && payload.buildId === pipeline.latestRun.id) {
                        matchFound = true;
                    }
                }
            });

            if (!matchFound && this._filteredPipelines) {
                this._filteredPipelines.forEach((pipeline: PipelineData) => {
                    if (payload.definitionId === pipeline.id) {
                        if (pipeline.latestRun && payload.buildId === pipeline.latestRun.id) {
                            matchFound = true;
                        }
                    }
                });
            }

            if (matchFound) {
                this._getUpdatedRun(payload.definitionId, payload.buildId);
            }
        }
    }

    private _updateStages = (payload: IStagesUpdatedPayload): void => {
        if (this._pipelines && this._pipelines.length > 0 && payload && payload.buildId && payload.stageUpdates) {
            const matchingPipeline = this._pipelines.find(p => p.latestRun && p.latestRun.id === payload.buildId);

            // only need to re-fetch run corresponding to stage update if current build status is
            // NotStarted or InProgress. NotStarted could also indicate that the pipeline
            // is pending on approval in this case
            if (matchingPipeline && (matchingPipeline.latestRun.status === BuildStatus.NotStarted || matchingPipeline.latestRun.status === BuildStatus.InProgress)) {
                this._getUpdatedRun(matchingPipeline.id, payload.buildId);
            }
        }
    }

    private _getUpdatedRun = (pipelineId: number, runId: number) => {
        if (this._runsToUpdate.indexOf(runId) === -1) {
            this._runsToUpdate.push(runId);
        }

        if (this._updateTimeout == undefined) {
            this._updateTimeout = setTimeout(this._fetchUpdatedRuns, PipelinesStore.s_batchingDelayInMs);
        }    
    }

    private _fetchUpdatedRuns = () => {
        const runsToUpdate = this._runsToUpdate;
        this._runsToUpdate = [];
        this._updateTimeout = undefined;

        if (runsToUpdate.length > 0) {
            this._contributionActionCreator.fetchContributionData("ms.vss-build-web.pipelines-data-provider", {"runIds": runsToUpdate.join(",")}, true, this._onUpdatedRunsAvailable); 
        } 
    }
}